import React from 'react';
import { AppTheme } from '../types';
import { ActionMenu } from './ActionMenu';
import { useLanguage } from '../contexts/LanguageContext';
import { formatDateTime } from '../utils/date';
import './Table.css';

interface TableColumn {
  key: string;
  title: string;
  width?: string;
}

interface TableProps {
  columns: TableColumn[];
  data: any[];
  theme: AppTheme;
  onView?: (item: any) => void;
  onEdit?: (item: any) => void;
  onDelete?: (item: any) => void;
  onAdd?: () => void;
  title: string;
  readOnly?: boolean;
}

export const Table: React.FC<TableProps> = ({
  columns,
  data,
  theme,
  onView,
  onEdit,
  onDelete,
  onAdd,
  title,
  readOnly = false
}) => {
  const { t } = useLanguage();

  return (
    <div className={`table-container ${theme.isDark ? 'dark' : 'light'}`}>
      <div className={`table-header ${theme.isDark ? 'dark' : 'light'}`}>
        <h3 className={`table-title ${theme.isDark ? 'dark' : 'light'}`}>{title}</h3>
        {onAdd && (
          <button className="table-add-button" onClick={onAdd}>
            + {t('table.add')}
          </button>
        )}
      </div>
      
      <table className="table-main">
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key} className={`table-th ${theme.isDark ? 'dark' : 'light'}`} style={{width: column.width}}>
                {column.title}
              </th>
            ))}
            {!readOnly && (
              <th className={`table-th ${theme.isDark ? 'dark' : 'light'}`} style={{width: '60px'}}>{t('table.actions')}</th>
            )}
          </tr>
        </thead>
        <tbody>
          {data.filter(item => !item.is_deleted).map((item) => (
            <tr key={item.id}>
              {columns.map((column) => {
                const value = item[column.key];
                let displayValue = value;

                // Форматирование даты для полей с датами
                if (column.key === 'created_at' || column.key.includes('date') || column.key.includes('Date')) {
                  displayValue = formatDateTime(value);
                }

                return (
                  <td key={column.key} className={`table-td ${theme.isDark ? 'dark' : 'light'}`}>
                    {column.key === 'logo_src' && value ? (
                      <img
                        src={value}
                        alt="Логотип банка"
                        className="table-logo"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                          const parent = (e.target as HTMLImageElement).parentElement;
                          if (parent) {
                            parent.textContent = '🏦';
                          }
                        }}
                      />
                    ) : column.key === 'logo_src' ? (
                      <span className="table-logo-fallback">🏦</span>
                    ) : (
                      displayValue
                    )}
                  </td>
                );
              })}
              {!readOnly && (
                <td className={`table-td ${theme.isDark ? 'dark' : 'light'}`}>
                  <ActionMenu
                    theme={theme}
                    items={[
                      ...(onView ? [{
                        label: t('table.view'),
                        icon: '👁️',
                        onClick: () => onView(item)
                      }] : []),
                      ...(onEdit ? [{
                        label: t('table.edit'),
                        icon: '✏️',
                        onClick: () => onEdit(item)
                      }] : []),
                      ...(onDelete ? [{
                        label: t('table.delete'),
                        icon: '🗑️',
                        onClick: () => onDelete(item),
                        variant: 'danger' as const
                      }] : [])
                    ]}
                  />
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      
      {data.filter(item => !item.is_deleted).length === 0 && (
        <div className={`table-empty ${theme.isDark ? 'dark' : 'light'}`}>
          Нет данных для отображения
        </div>
      )}
    </div>
  );
};